import { Attachment } from '../shared/attachment';
import { EntityCommandRequest } from '../entities/views/entity.view.request';
import { AIProcessInboxMetadataInfo, AIProcessInboxStatus } from '../../ai/ai.process.inbox';
import { Bill } from './invoicing';
import { InvoiceProcessingRequest, InvoicingCSVDownloadRequest } from './invoicing.request';
import { FinancialDocumentAllocateRequest } from './financial.document.allocation';
import { FinancialDocumentPaymentPrepareRequest, FinancialDocumentPaymentRecordRequest } from './financial.document.payment';


export interface ProcessCreateFromAICommandRequest extends EntityCommandRequest {
    commandId: 'ProcessCreateFromAI'
    data: {
        processId?: string;
        files?: Array<Attachment>;
        metadata?: AIProcessInboxMetadataInfo
    }
}

export interface RetryCreateFromAICommandRequest extends EntityCommandRequest {
    commandId: 'RetryCreateFromAI'
    data: {
        processId: string;
    }
}

export interface CancelCreateFromAICommandRequest extends EntityCommandRequest {
    commandId: 'CancelCreateFromAI'
    data: {
        processId: string;
    }
}

export interface DeleteCreateFromAICommandRequest extends EntityCommandRequest {
    commandId: 'DeleteCreateFromAI'
    data: {
        processId: string;
    }
}

export interface GetExtractedResultCommandRequest extends EntityCommandRequest {
    commandId: 'GetExtractedResult'
    data: {
        processId: string;
    }
}

export interface BillCreateAndSendCommandRequest extends EntityCommandRequest {
    commandId: 'CreateAndSend'
    data: {
        bill: Bill;
    }
}

/** Entity-data command id. Same file as legacy Download CSV; not `/api/finance/download/bills/csv`. */
export const BILL_EXPORT_CSV_COMMAND_ID = 'ExportCsv';

export interface BillCsvExport {
    filename: string;
    content: string;
    contentType: 'text/csv;charset=utf-8';
}

export interface ExportBillsCsvCommandRequest extends EntityCommandRequest {
    commandId: typeof BILL_EXPORT_CSV_COMMAND_ID;
    data: InvoicingCSVDownloadRequest;
}

export interface ProcessCreateFromAICommandResponse {
    success: boolean;
    fileCount: number;
    invalidFileCount: number;
    /** Set when the scan was written to the durable AI jobs queue: the placeholder process record and its outbox record. */
    processId?: string;
    outboxId?: string;
}

/** Result of a bill scan retry; the same inbox is retained and either a native run or queued job owns its next execution. */
export interface RetryCreateFromAICommandResponse {
    processId: string;
    workflowRunId?: string;
    outboxId?: string;
    status: AIProcessInboxStatus;
    fileCount: number;
    isRetry: true;
    retryCount: number;
}

/** Named lifecycle + processing command ids for Bill entity-data (mirror InvoiceCommandId casing). */
export enum BillCommandId {
  CreateAndSend = 'CreateAndSend',
  Processing = 'Processing',
  Approve = 'approve',
  ApproveAndSend = 'approveAndSend',
  Void = 'void',
  Retry = 'retry',
  Reissue = 'reissue',
  PreparePayment = 'preparePayment',
  RecordPayment = 'recordPayment',
  PrepareAllocation = 'prepareAllocation',
  AllocateCredit = 'allocateCredit',
}

export type BillNamedLifecycleRequest = { invoiceId: string };

export type BillCommandRequest =
    | ProcessCreateFromAICommandRequest
    | RetryCreateFromAICommandRequest
    | CancelCreateFromAICommandRequest
    | DeleteCreateFromAICommandRequest
    | GetExtractedResultCommandRequest
    | BillCreateAndSendCommandRequest
    | ExportBillsCsvCommandRequest
    | { commandId: BillCommandId.Approve; data: BillNamedLifecycleRequest }
    | { commandId: BillCommandId.ApproveAndSend; data: BillNamedLifecycleRequest }
    | { commandId: BillCommandId.Void; data: BillNamedLifecycleRequest }
    | { commandId: BillCommandId.Retry; data: BillNamedLifecycleRequest }
    | { commandId: BillCommandId.Reissue; data: BillNamedLifecycleRequest }
    | { commandId: BillCommandId.Processing; data: InvoiceProcessingRequest }
    | { commandId: BillCommandId.PreparePayment; data: FinancialDocumentPaymentPrepareRequest }
    | { commandId: BillCommandId.RecordPayment; data: FinancialDocumentPaymentRecordRequest }
    | { commandId: BillCommandId.PrepareAllocation; data: FinancialDocumentPaymentPrepareRequest }
    | { commandId: BillCommandId.AllocateCredit; data: FinancialDocumentAllocateRequest };
